import { Controller } from "@hotwired/stimulus";

const EXPAND_EVENT = "question-card:expand";
const COLLAPSE_EVENT = "question-card:collapse";
const SETTINGS_EVENT = "question-card:settings";

export default class extends Controller<HTMLElement> {
  static targets = ["display", "form", "titleInput"];

  declare readonly displayTarget: HTMLElement;
  declare readonly formTarget: HTMLElement;
  declare readonly hasFormTarget: boolean;
  declare readonly hasTitleInputTarget: boolean;
  declare readonly titleInputTarget: HTMLInputElement;

  private boundOnOtherExpand = this.onOtherExpand.bind(this);
  // Set when the user expands before the lazy-loaded form has arrived, so
  // titleInputTargetConnected can complete the focus once it lands.
  private pendingTitleFocus = false;
  // A form-less (locked) card has no open form to check, so remember that
  // it is the focused card and don't re-dispatch on repeat clicks.
  private focused = false;

  connect() {
    window.addEventListener(EXPAND_EVENT, this.boundOnOtherExpand);
  }

  disconnect() {
    window.removeEventListener(EXPAND_EVENT, this.boundOnOtherExpand);
  }

  // The auto-save turbo_stream replaces the display row with the partial
  // rendered as `show_form_open: true` — it assumes the form is still open
  // when the response paints. That can be stale: another card may have
  // expanded mid-save and silently collapsed this one, so the new display
  // arrives pre-stamped with `hidden` while the form is also hidden, leaving
  // the card empty. Reconcile against the actual form state every time the
  // display (re)connects.
  displayTargetConnected(element: HTMLElement) {
    if (!this.hasFormTarget) return;

    const formIsHidden = this.formTarget.classList.contains("hidden");
    element.classList.toggle("hidden", !formIsHidden);
  }

  expand(event: Event) {
    event.preventDefault();
    if (this.hasFormTarget ? !this.formTarget.classList.contains("hidden") : this.focused) return;

    window.dispatchEvent(
      new CustomEvent(EXPAND_EVENT, {
        detail: {
          source: this.element,
          questionId: this.element.dataset.questionId,
          sectionId: this.element.dataset.sectionId,
        },
      }),
    );
    // A locked (published) survey renders the card without a form: the
    // event above focuses it in the outline and rail, and that's all.
    this.focused = true;
    if (!this.hasFormTarget) return;
    this.displayTarget.classList.add("hidden");
    this.formTarget.classList.remove("hidden");
    if (this.hasTitleInputTarget) {
      this.titleInputTarget.focus();
    } else {
      // First expand: unhiding the container triggers the lazy Turbo Frame
      // load; focus when the title input connects.
      this.pendingTitleFocus = true;
    }
  }

  titleInputTargetConnected(element: HTMLInputElement) {
    if (!this.pendingTitleFocus) return;
    this.pendingTitleFocus = false;
    element.focus();
  }

  // Public action — the user minimized the card. Collapse it and tell the
  // context rail to drop this question's settings (back to the placeholder).
  collapse() {
    if (this.collapseForm()) {
      window.dispatchEvent(new CustomEvent(COLLAPSE_EVENT));
    }
  }

  // Public action — the mobile "Settings" button on the open card. Tells the
  // context rail to load this question's settings and reveal the bottom sheet.
  // On desktop the rail already reflects the focused question, so this button
  // is hidden there.
  openSettings() {
    window.dispatchEvent(
      new CustomEvent(SETTINGS_EVENT, {
        detail: {
          questionId: this.element.dataset.questionId,
          sectionId: this.element.dataset.sectionId,
        },
      }),
    );
  }

  stop(event: Event) {
    event.stopPropagation();
  }

  private onOtherExpand(event: Event) {
    const detail = (event as CustomEvent).detail;
    if (detail?.source === this.element) return;
    this.focused = false;
    // Another card took focus — collapse silently; the rail is already
    // loading the other card's settings, so it must not be reset.
    this.collapseForm();
  }

  // Hide the form, restore the display row. Returns true if it was open.
  private collapseForm(): boolean {
    if (!this.hasFormTarget) return false;
    if (this.formTarget.classList.contains("hidden")) return false;
    this.formTarget.classList.add("hidden");
    this.displayTarget.classList.remove("hidden");
    return true;
  }
}
